import {
	JobReview,
	JobReviewFact,
	JobReviewLegalRequest,
	JobReviewThesis,
	JobReviewTopic,
	RejectThesisInput,
} from 'modules/contestacao/pages/review/contestacao-review.model';
import { ContestacaoTemplateThesisEntry } from 'services/contestacao-template/contestacao-template.model';
import { MockHttpError, nowIso } from '../mock-api';
import { currentJobStatus, findJobEntry, setJobStatus } from '../shared/mock-job-state';
import { activeTemplateFor } from './contestacao-templates';
import { currentMockUser } from './contestacao-user';
import {
	CASE_PROFILES,
	daysAgoIso,
	DEFAULT_CASE_FACTS,
	DEFAULT_SUGGESTED,
	GENERIC_JUSTIFICATIONS,
	MockCaseProfile,
	REVIEW_PRESETS,
	THESIS_TEXTS,
	ThesisRosterId,
} from './contestacao.const';

/** Revisões em memória por job — montadas sob demanda a partir do template publicado */
const reviews = new Map<string, JobReview>();
/** Pedidos da revisão (com as teses vinculadas) por job — `willBeApplied` é recalculado a cada resposta */
const legalRequestLinks = new Map<string, Map<string, JobReviewLegalRequest>>();

function isRosterThesis(thesisId: string): thesisId is ThesisRosterId {
	return thesisId in THESIS_TEXTS;
}

function formatFactValue(value: string | boolean | undefined): string {
	if (value === undefined) return '—';
	if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
	const isoDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
	return isoDate ? `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}` : value;
}

function fillVariables(content: string, facts: MockCaseProfile['facts']): string {
	return content.replace(/\{\{([a-z0-9-]+)\}\}/g, (token, slug: string) =>
		slug in facts ? formatFactValue(facts[slug as keyof typeof facts]) : token,
	);
}

/** Número CNJ determinístico para jobs criados durante a demo */
function caseNumberFor(jobId: string): string {
	const digits = [...jobId].reduce((sum, char) => (sum * 31 + char.charCodeAt(0)) % 9_999_999, 7);
	return `${String(digits).padStart(7, '0')}-${String(digits % 97).padStart(2, '0')}.2026.5.02.00${String((digits % 89) + 10)}`;
}

function profileFor(jobId: string): MockCaseProfile {
	return (
		CASE_PROFILES[jobId] ?? {
			caseNumber: caseNumberFor(jobId),
			facts: DEFAULT_CASE_FACTS,
			suggested: DEFAULT_SUGGESTED,
			justifications: {},
		}
	);
}

function buildThesis(
	entry: ContestacaoTemplateThesisEntry,
	profile: MockCaseProfile,
	order: number,
): JobReviewThesis {
	const rosterId = isRosterThesis(entry.id) ? entry.id : null;
	const suggested = rosterId ? profile.suggested.includes(rosterId) : false;
	const content = fillVariables(entry.content, profile.facts);
	const facts: JobReviewFact[] = entry.facts.map((fact) => ({
		id: fact.id,
		key: fact.slug,
		classification: fact.classification,
		schema: { ...fact.schema },
		result: profile.facts[fact.slug as keyof MockCaseProfile['facts']] ?? null,
	}));
	return {
		id: entry.id,
		key: entry.slug,
		name: entry.name,
		originalContent: content,
		content,
		facts,
		aiSuggestedApproval: suggested,
		aiJustification:
			suggested && rosterId
				? (profile.justifications[rosterId] ?? GENERIC_JUSTIFICATIONS[rosterId])
				: null,
		justification: null,
		tags: entry.tags,
		// Teses não sugeridas chegam rejeitadas (ocultas) — viram candidatas à adição manual
		reviewStatus: suggested ? 'PENDING' : 'REJECTED',
		rejectionReason: null,
		rejectionDescription: null,
		userOrder: order,
		reviewedBy: null,
		reviewedAt: null,
	};
}

function allTheses(review: JobReview): JobReviewThesis[] {
	return review.topics.flatMap((topic) => topic.theses);
}

/** Pedidos derivados das teses: aplicado quando alguma tese vinculada está aprovada */
function refreshLegalRequests(review: JobReview, links: Map<string, JobReviewLegalRequest>) {
	const theses = allTheses(review);
	review.legalRequests = [...links.values()].map((legalRequest) => ({
		...legalRequest,
		willBeApplied: theses.some(
			(thesis) => legalRequest.thesisIds.includes(thesis.id) && thesis.reviewStatus === 'APPROVED',
		),
	}));
}

function buildReview(jobId: string): JobReview {
	const job = findJobEntry(jobId);
	if (!job || job.product !== 'contestacao') {
		throw new MockHttpError(
			404,
			'Revisão não encontrada',
			'Este job não possui revisão de Contestação.',
		);
	}
	const template = activeTemplateFor(job.productSetupId);
	const profile = profileFor(jobId);
	const links = new Map<string, JobReviewLegalRequest>();

	const topics: JobReviewTopic[] = [...template.topics]
		.sort((first, second) => first.order - second.order)
		.map((topic) => ({
			id: topic.id,
			name: topic.name,
			order: topic.order,
			theses: [...topic.theses]
				.sort((first, second) => first.order - second.order)
				.map((thesis, index) => {
					for (const legalRequest of thesis.legalRequests) {
						const link = links.get(legalRequest.id) ?? {
							id: legalRequest.id,
							name: legalRequest.name,
							slug: legalRequest.slug,
							description: legalRequest.description,
							order: links.size,
							thesisIds: [],
							willBeApplied: false,
						};
						link.thesisIds.push(thesis.id);
						links.set(legalRequest.id, link);
					}
					return buildThesis(thesis, profile, index);
				}),
		}));

	const review: JobReview = {
		template: `${template.name} — v${template.version}`,
		case: profile.caseNumber,
		topics,
		legalRequests: [],
		// O modo documento monta as seções da config e preenche as variáveis com os fatos.
		config: template.config,
		facts: Object.fromEntries(
			Object.entries(profile.facts).map(([slug, value]) => [slug, { result: value }]),
		),
		openedAt: null,
		openedBy: null,
		completedAt: null,
		completedBy: null,
	};

	applyPreset(jobId, review);
	refreshLegalRequests(review, links);
	legalRequestLinks.set(jobId, links);
	return review;
}

function applyPreset(jobId: string, review: JobReview) {
	const preset = REVIEW_PRESETS[jobId];
	if (!preset) return;
	review.openedAt = daysAgoIso(preset.openedDaysAgo, 9, 12);
	review.openedBy = preset.openedBy.name;
	if (preset.completedDaysAgo !== undefined) {
		review.completedAt = daysAgoIso(preset.completedDaysAgo, 17, 40);
		review.completedBy = preset.openedBy.name;
	}
	let minute = 15;
	for (const thesis of allTheses(review)) {
		const decision = isRosterThesis(thesis.id) ? preset.decisions[thesis.id] : undefined;
		if (!decision) continue;
		minute += 4;
		thesis.reviewStatus = decision.status;
		thesis.reviewedBy = preset.openedBy;
		thesis.reviewedAt = daysAgoIso(preset.openedDaysAgo, 9, minute);
		if (decision.manual) thesis.aiSuggestedApproval = false;
		if (decision.status === 'REJECTED') {
			thesis.rejectionReason = decision.reason ?? null;
			thesis.rejectionDescription = decision.description ?? null;
		}
		if (decision.appendedContent) thesis.content = `${thesis.content}${decision.appendedContent}`;
	}
	// Revisão concluída: o que ficou pendente é considerado rejeitado
	if (review.completedAt) {
		for (const thesis of allTheses(review)) {
			if (thesis.reviewStatus === 'PENDING') thesis.reviewStatus = 'REJECTED';
		}
	}
}

// ── Acesso ───────────────────────────────────────────────────────────────────

export function reviewOf(jobId: string): JobReview {
	let review = reviews.get(jobId);
	if (!review) {
		review = buildReview(jobId);
		reviews.set(jobId, review);
	}
	return review;
}

function respond(jobId: string): JobReview {
	const review = reviewOf(jobId);
	const links = legalRequestLinks.get(jobId);
	if (links) refreshLegalRequests(review, links);
	return structuredClone(review);
}

function editable(jobId: string): JobReview {
	const review = reviewOf(jobId);
	if (review.completedAt || currentJobStatus(jobId) === 'Finalizado') {
		throw new MockHttpError(
			409,
			'Revisão concluída',
			'Esta revisão já foi concluída e está disponível apenas para leitura.',
		);
	}
	return review;
}

function thesisOf(review: JobReview, thesisId: string): JobReviewThesis {
	const thesis = allTheses(review).find((item) => item.id === thesisId);
	if (!thesis)
		throw new MockHttpError(404, 'Tese não encontrada', 'A tese não faz parte desta revisão.');
	return thesis;
}

function markReviewed(thesis: JobReviewThesis) {
	thesis.reviewedBy = currentMockUser();
	thesis.reviewedAt = nowIso();
}

// ── Ações ────────────────────────────────────────────────────────────────────

export function getReview(jobId: string): JobReview {
	return respond(jobId);
}

export function openReview(jobId: string): JobReview {
	const review = reviewOf(jobId);
	if (!review.openedAt) {
		review.openedAt = nowIso();
		review.openedBy = currentMockUser().name;
	}
	if (currentJobStatus(jobId) === 'Aguardando revisão') setJobStatus(jobId, 'Em revisão');
	return respond(jobId);
}

export function completeReview(jobId: string): JobReview {
	const review = editable(jobId);
	const theses = allTheses(review);
	if (theses.some((thesis) => thesis.reviewStatus === 'PENDING')) {
		throw new MockHttpError(
			422,
			'Revisão incompleta',
			'Revise todas as teses antes de concluir a revisão.',
		);
	}
	review.completedAt = nowIso();
	review.completedBy = currentMockUser().name;
	const approved = theses.filter((thesis) => thesis.reviewStatus === 'APPROVED').length;
	setJobStatus(jobId, 'Finalizado', {
		selection: {
			warnings: [],
			total_theses: theses.length,
			approved_count: approved,
			rejected_count: theses.length - approved,
		},
	});
	return respond(jobId);
}

export function approveThesis(jobId: string, thesisId: string): JobReview {
	const thesis = thesisOf(editable(jobId), thesisId);
	thesis.reviewStatus = 'APPROVED';
	thesis.rejectionReason = null;
	thesis.rejectionDescription = null;
	markReviewed(thesis);
	return respond(jobId);
}

export function rejectThesis(jobId: string, thesisId: string, body: RejectThesisInput): JobReview {
	const thesis = thesisOf(editable(jobId), thesisId);
	thesis.reviewStatus = 'REJECTED';
	thesis.rejectionReason = body.reason;
	thesis.rejectionDescription = body.description ?? null;
	markReviewed(thesis);
	return respond(jobId);
}

/** Remover = rejeitar e tirar da lista: a tese volta ao conjunto da adição manual */
export function removeThesis(jobId: string, thesisId: string, body: RejectThesisInput): JobReview {
	const thesis = thesisOf(editable(jobId), thesisId);
	thesis.reviewStatus = 'REMOVED';
	thesis.aiSuggestedApproval = false;
	thesis.rejectionReason = body.reason;
	thesis.rejectionDescription = body.description ?? null;
	markReviewed(thesis);
	return respond(jobId);
}

/** Reenviar o conteúdo original volta a tese ao estado sem revisão */
export function editThesisContent(jobId: string, thesisId: string, content: string): JobReview {
	const thesis = thesisOf(editable(jobId), thesisId);
	thesis.content = content;
	if (content === thesis.originalContent) {
		thesis.reviewStatus = 'PENDING';
		thesis.rejectionReason = null;
		thesis.rejectionDescription = null;
		thesis.reviewedBy = null;
		thesis.reviewedAt = null;
	}
	return respond(jobId);
}

export function bulkApproveTopic(jobId: string, topicId: string): JobReview {
	const review = editable(jobId);
	const topic = review.topics.find((item) => item.id === topicId);
	if (!topic)
		throw new MockHttpError(404, 'Tópico não encontrado', 'O tópico não faz parte desta revisão.');
	for (const thesis of topic.theses) {
		if (thesis.reviewStatus !== 'PENDING') continue;
		thesis.reviewStatus = 'APPROVED';
		markReviewed(thesis);
	}
	return respond(jobId);
}

export function reorderTheses(
	jobId: string,
	topicId: string,
	orderedThesisIds: string[],
): JobReview {
	const review = editable(jobId);
	const topic = review.topics.find((item) => item.id === topicId);
	if (!topic)
		throw new MockHttpError(404, 'Tópico não encontrado', 'O tópico não faz parte desta revisão.');
	const position = (thesis: JobReviewThesis) => {
		const index = orderedThesisIds.indexOf(thesis.id);
		return index === -1 ? orderedThesisIds.length + thesis.userOrder : index;
	};
	topic.theses = [...topic.theses].sort((first, second) => position(first) - position(second));
	topic.theses.forEach((thesis, index) => (thesis.userOrder = index));
	return respond(jobId);
}
